import React from 'react';
import DiscoverHeader from './HeaderDiscover';
import Footer from '../Footer';
import EventSection from '../event/EventSection';
import FeaturedEventsSection from '../event/FeaturedEventsSection';
import GuideSection from './GuideSection';
import RelatedGuides from './RelatedGuides';
import { pagesContent, topicOverrides, type PageContent } from '../../content/discover/content';
import type { GuidePoint } from '../../content/discover/content';
import { pagesEvents } from '../../content/discover/events';
import { getTopicCopyIfExists, type TopicCopy, type TopicSlug } from '../../content/discover/topics';
import EventTranslationProvider from '../translation/EventTranslationProvider';

type FaqItem = {
  question: string;
  answer: React.ReactNode;
};

type DiscoverTopicPageProps = {
  /** Topic slug; must exist in src/content/discover/topics.ts. */
  contentKey: TopicSlug;
  /** Key in `pagesEvents` for the static event grid section. Defaults to `tokyo-local-food-experiences`. */
  eventsKey?: string;
  /** Featured section heading text (H2). */
  featuredHeading: string;
  /** Featured section intro paragraph under the heading. */
  featuredIntro: string;
  /** FAQ heading text (H2). */
  faqHeading: string;
  /** FAQ items rendered under the heading. */
  faqData: FaqItem[];
};

const DEFAULT_SECTION_CONTAINER = "container mx-auto px-4 md:px-8 w-full max-w-[1280px]";

type SimpleGuide = {
  href: string;
  label: string;
  image?: string;
};

const toTitle = (key: string) =>
  key
    .split('-')
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const inferCity = (key: string): string => {
  const first = key.split('-')[0];
  if (first === 'tokyo') return 'Tokyo';
  if (first === 'kyoto') return 'Kyoto';
  if (first === 'osaka') return 'Osaka';
  if (first === 'japan') return 'Japan';
  return 'Japan';
};

const hrefForSlug = (slug: string) => `/us/discover/${slug}`;

const imageForSlug = (slug: string): string => {
  if (slug.includes('anime') || slug.includes('otaku')) return '/host/images/discover-images/guide-anime-events-nyc.webp';
  if (slug.includes('festival') || slug.includes('celebration')) return '/host/images/discover-images/hero-japanese-festivals-nyc.webp';
  if (slug.includes('sake') || slug.includes('brewery') || slug.includes('nightlife') || slug.includes('bar')) return '/host/images/discover-images/hero-japanese-music-nyc.webp';
  if (slug.includes('culture') || slug.includes('tradition')) return '/host/images/discover-images/guide-japanese-crafts-nyc.webp';
  if (slug.includes('food') || slug.includes('drink') || slug.includes('eat')) return '/host/images/discover-images/guide-japanese-music-nyc.webp';
  return '/host/images/discover-images/guide-japanese-crafts-nyc.webp';
};

const labelForSlug = (slug: string): string => {
  const city = inferCity(slug);
  const topic = toTitle(slug.replace(/^(tokyo|kyoto|osaka|japan)-/, ''));
  if (city === 'Japan') return `Japan: ${topic}`;
  return `${city}: ${topic}`;
};

const guide = (slug: string): SimpleGuide => ({
  href: hrefForSlug(slug),
  label: labelForSlug(slug),
  image: imageForSlug(slug),
});

const defaultRelatedSlugsFor = (contentKey: string): string[] => {
  // Keep within the 18 pages we have.
  switch (contentKey) {
    case 'tokyo-local-food-experiences':
      return ['tokyo-food-and-drink', 'tokyo-sake-and-brewery-tours', 'tokyo-traditions-and-culture', 'tokyo-seasonal-festivals-and-events'];

    case 'tokyo-traditions-and-culture':
      return ['tokyo-seasonal-festivals-and-events', 'tokyo-nightlife-and-bar-scene', 'tokyo-anime-and-otaku-culture', 'tokyo-food-and-drink'];
    case 'tokyo-food-and-drink':
      return ['tokyo-local-food-experiences', 'tokyo-sake-and-brewery-tours', 'tokyo-nightlife-and-bar-scene', 'tokyo-seasonal-festivals-and-events'];
    case 'tokyo-anime-and-otaku-culture':
      return ['japan-anime-and-otaku', 'japan-top-experiences', 'tokyo-traditions-and-culture', 'tokyo-nightlife-and-bar-scene'];
    case 'tokyo-nightlife-and-bar-scene':
      return ['tokyo-sake-and-brewery-tours', 'tokyo-food-and-drink', 'tokyo-seasonal-festivals-and-events', 'japan-top-experiences'];
    case 'tokyo-sake-and-brewery-tours':
      return ['tokyo-food-and-drink', 'tokyo-nightlife-and-bar-scene', 'tokyo-local-food-experiences', 'japan-food-and-drink-experiences'];
    case 'tokyo-seasonal-festivals-and-events':
      return ['japan-festivals-and-celebrations', 'tokyo-traditions-and-culture', 'kyoto-traditions-and-culture', 'japan-top-experiences'];

    case 'kyoto-local-food-experiences':
      return ['kyoto-culture-and-experiences', 'kyoto-traditions-and-culture', 'osaka-local-food-experiences', 'japan-food-and-drink-experiences'];
    case 'kyoto-traditions-and-culture':
      return ['kyoto-culture-and-experiences', 'tokyo-traditions-and-culture', 'japan-culture-and-experiences', 'kyoto-local-food-experiences'];
    case 'kyoto-culture-and-experiences':
      return ['kyoto-traditions-and-culture', 'japan-culture-and-experiences', 'japan-top-experiences', 'kyoto-local-food-experiences'];

    case 'osaka-local-food-experiences':
      return ['osaka-food-and-drink', 'osaka-traditions-and-culture', 'kyoto-local-food-experiences', 'japan-food-and-drink-experiences'];
    case 'osaka-food-and-drink':
      return ['osaka-local-food-experiences', 'tokyo-food-and-drink', 'japan-food-and-drink-experiences', 'tokyo-nightlife-and-bar-scene'];
    case 'osaka-traditions-and-culture':
      return ['osaka-local-food-experiences', 'kyoto-traditions-and-culture', 'japan-culture-and-experiences', 'tokyo-traditions-and-culture'];

    case 'japan-top-experiences':
      return ['japan-food-and-drink-experiences', 'japan-culture-and-experiences', 'japan-festivals-and-celebrations', 'japan-anime-and-otaku'];
    case 'japan-food-and-drink-experiences':
      return ['tokyo-food-and-drink', 'osaka-food-and-drink', 'kyoto-local-food-experiences', 'japan-top-experiences'];
    case 'japan-culture-and-experiences':
      return ['tokyo-traditions-and-culture', 'kyoto-culture-and-experiences', 'osaka-traditions-and-culture', 'japan-top-experiences'];
    case 'japan-anime-and-otaku':
      return ['tokyo-anime-and-otaku-culture', 'japan-top-experiences', 'japan-festivals-and-celebrations', 'japan-culture-and-experiences'];
    case 'japan-festivals-and-celebrations':
      return ['tokyo-seasonal-festivals-and-events', 'japan-top-experiences', 'japan-culture-and-experiences', 'tokyo-traditions-and-culture'];

    default:
      return ['japan-top-experiences', 'japan-food-and-drink-experiences', 'japan-culture-and-experiences', 'japan-festivals-and-celebrations'];
  }
};

const defaultRelatedGuidesFor = (contentKey: string): SimpleGuide[] =>
  defaultRelatedSlugsFor(contentKey).map(guide);

const DEFAULT_GUIDE_ICON = '/host/images/discover-images/icon_fox_head.svg';
const DEFAULT_HERO_IMAGE = '/host/images/discover-images/hero-eat-like-a-local.svg';
const DEFAULT_GUIDE_IMAGE = '/host/images/discover-images/guide-japanese-crafts-nyc.webp';
const DEFAULT_OVERLAY_LOGO = '/host/images/discover-images/visiting-japan-logo.svg';

/** Slugs that have custom -hero.svg and -body.svg deployed to production. Others use default hero/guide images so no broken images. */
const SLUGS_WITH_CUSTOM_IMAGES = new Set<string>([
  'tokyo-local-food-experiences',
  'tokyo-traditions-and-culture',
  'tokyo-food-and-drink',
  'tokyo-anime-and-otaku-culture',
  'tokyo-nightlife-and-bar-scene',
  'tokyo-sake-and-brewery-tours',
  'tokyo-seasonal-festivals-and-events',
  'kyoto-local-food-experiences',
  'kyoto-traditions-and-culture',
  'kyoto-culture-and-experiences',
  'osaka-local-food-experiences',
  'osaka-food-and-drink',
  'osaka-traditions-and-culture',
]);

/** Build full PageContent from topic copy (source of truth) plus optional layout overrides (e.g. from content.ts). */
function buildPageContentFromTopicCopy(
  topicCopy: TopicCopy,
  slug: TopicSlug,
  featuredHeading: string,
  featuredIntro: string,
  layoutOverrides?: Partial<PageContent>
): PageContent {
  const city = inferCity(slug);
  const topic = toTitle(slug.replace(/^(tokyo|kyoto|osaka|japan)-/, ''));
  const hero = layoutOverrides?.hero;
  const guideLayout = layoutOverrides?.guide;
  // Image naming: <slug>-hero.svg and <slug>-body.svg.
  const imageSlug = slug;
  const useCustomImages = SLUGS_WITH_CUSTOM_IMAGES.has(imageSlug);
  const defaultHeroImage = useCustomImages
    ? `/host/images/discover-images/${imageSlug}-hero.svg`
    : DEFAULT_HERO_IMAGE;
  const defaultGuideImage = useCustomImages
    ? `/host/images/discover-images/${imageSlug}-body.svg`
    : DEFAULT_GUIDE_IMAGE;
  const points: GuidePoint[] = topicCopy.guide.bullets.map((b) => ({
    icon: DEFAULT_GUIDE_ICON,
    title: b.title,
    description: b.description,
  }));
  return {
    meta: layoutOverrides?.meta ?? {
      title: `${topicCopy.hero.headline} | Peatix`,
      description: topicCopy.hero.description,
    },
    hero: {
      description: topicCopy.hero.description,
      city,
      heroImage: hero?.heroImage ?? defaultHeroImage,
      overlayLogo: hero?.overlayLogo ?? DEFAULT_OVERLAY_LOGO,
      headline: topicCopy.hero.headline,
      headlineHighlight: topicCopy.hero.headlineHighlight,
    },
    statisticsAudience: layoutOverrides?.statisticsAudience ?? 'culture',
    highlightsImage: layoutOverrides?.highlightsImage,
    guide: {
      introParagraph: topicCopy.guide.intro,
      headerIcon: guideLayout?.headerIcon ?? DEFAULT_GUIDE_ICON,
      bulletStyle: guideLayout?.bulletStyle ?? 'standard',
      points,
      guideImage: guideLayout?.guideImage ?? defaultGuideImage,
      closingText: topicCopy.guide.closingText,
    },
    eventSectionDescription: layoutOverrides?.eventSectionDescription ?? {
      title: featuredHeading,
      description: featuredIntro,
    },
    featuresTarget: layoutOverrides?.featuresTarget ?? topic,
    relatedGuides: layoutOverrides?.relatedGuides,
    featuredSection: layoutOverrides?.featuredSection,
  };
}

export default function DiscoverTopicPage({
  contentKey,
  eventsKey = 'tokyo-local-food-experiences',
  featuredHeading,
  featuredIntro,
  faqHeading,
  faqData,
}: DiscoverTopicPageProps) {
  const topicCopy = getTopicCopyIfExists(contentKey);
  if (!topicCopy) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center">
        <p className="text-red-600 font-medium" role="alert">
          Missing topic copy for <code className="bg-gray-100 px-1 rounded">{contentKey}</code>. Add an entry to{' '}
          <code className="bg-gray-100 px-1 rounded">src/content/discover/topics.ts</code>.
        </p>
      </div>
    );
  }
  const layoutOverrides = (pagesContent[contentKey] ?? topicOverrides[contentKey]) as Partial<PageContent> | undefined;
  const content = buildPageContentFromTopicCopy(topicCopy, contentKey, featuredHeading, featuredIntro, layoutOverrides);
  const events = pagesEvents[eventsKey];
  const relatedGuides: SimpleGuide[] =
    content.relatedGuides && content.relatedGuides.length
      ? content.relatedGuides
      : defaultRelatedGuidesFor(contentKey);

  return (
    <div className="min-h-screen flex flex-col">
      <DiscoverHeader content={content} titleTag="h2" />
      <main className="flex-grow">
        <EventTranslationProvider>
          {({ onEventsFetched, eventsOverride, translateButton }) => (
            <FeaturedEventsSection
              beforeContent={
                <div>
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="flex items-center gap-3">
                      <img
                        src="/host/images/discover-images/icon_sushi.svg"
                        alt=""
                        className="w-10 h-10 flex-shrink-0"
                        aria-hidden
                      />
                      <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 text-left">
                        {featuredHeading}
                      </h2>
                    </div>
                    {translateButton}
                  </div>
                  <p className="text-left text-gray-600 text-lg leading-relaxed max-w-3xl mb-12 md:mb-16">
                    {featuredIntro}
                  </p>
                </div>
              }
              fetchMode={content.featuredSection?.fetchMode || 'search'}
              containerClassName={DEFAULT_SECTION_CONTAINER}
              onEventsFetched={onEventsFetched}
              eventsOverride={eventsOverride}
              searchQuery={content.featuredSection?.searchQuery || {
                countryCode: 'jp',
                type: 'physical',
                sort: 'datetime asc',
                tagIds: [2803, 2826, 5342, 5497, 3578, 2594, 2720, 5466, 2650],
              }}
              itemsPerPage={content.featuredSection?.itemsPerPage || 6}
            />
          )}
        </EventTranslationProvider>

        <EventSection
          events={events}
          containerClassName={`${DEFAULT_SECTION_CONTAINER} pt-0 pb-16`}
        />

        {/* FAQ Section */}
        <section className="bg-white py-16">
          <div className={DEFAULT_SECTION_CONTAINER}>
            <div className="flex items-center gap-3 mb-8">
              <img
                src="/host/images/discover-images/icon_tanuki.svg"
                alt=""
                className="w-10 h-10 flex-shrink-0"
                aria-hidden
              />
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 text-left">
                {faqHeading}
              </h2>
            </div>
            <div className="space-y-8">
              {faqData.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GuideSection
          points={content.guide.points}
          guideImage={content.guide.guideImage}
          closingText={content.guide.closingText}
          introParagraph={content.guide.introParagraph}
          headerIcon={content.guide.headerIcon}
          bulletStyle={content.guide.bulletStyle}
          sectionClassName="bg-white"
          containerClassName={`${DEFAULT_SECTION_CONTAINER} py-16`}
        />

        {/* Planning your Japan itinerary — heading + guide cards */}
        {relatedGuides.length ? (
          <section className="bg-white">
            <div className={`${DEFAULT_SECTION_CONTAINER} py-12`}>
              <RelatedGuides
                heading="Planning your Japan itinerary"
                headingIcon="/host/images/discover-images/icon_fox_head_pea.svg"
                subtitle="Explore more experiences, compare options, and get your tickets before popular events sell out."
                guides={relatedGuides}
                className="!mt-0 !pt-0 !border-t-0"
                headingClassName="text-3xl md:text-4xl font-semibold text-gray-900 text-left mb-5"
                subtitleClassName="text-left text-gray-600 text-lg mb-12 md:mb-16"
              />
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}

